/**
 * Shared "managed list" UX for the four settings lookup pages:
 *
 *   - Correction reasons (``/settings/correction-reasons``)
 *   - Payment methods    (``/settings/payment-methods``)
 *   - Expense categories (``/settings/expense-categories``)
 *   - Pfand values       (``/settings/pfand-values``)
 *
 * Every page renders an AntD ``<Table>`` with:
 *
 *   1. drag handle (rows reorder via react-dnd; non-archived only)
 *   2. label cell + inline ``system`` tag where ``isSystem`` is true
 *   3. ...domain-specific ``extraColumns`` (e.g. Pfand cents)
 *   4. action cell with Edit / Archive (Restore on archived rows)
 *
 * A collapsible "Archived (N)" section sits underneath the active rows.
 * Archived rows have no drag handle and an explicit Restore button.
 *
 * The component owns reorder + collapse UI state only; create/edit modals,
 * mutations, and the source data live in the parent page so per-entity
 * fields (Pfand cents vs Payment-method name vs Correction-reason label)
 * stay local to the page that knows about them.
 *
 * DnD pattern is the catalog one trimmed to a single drag kind ("managed
 * list row") — same ``liveRef`` + stable ``useDrag/useDrop`` deps + state
 * + ref ``dropPos`` for the live insertion indicator, ``MultiBackend``
 * wired by the parent.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { DragOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { useStableTableComponents } from "../workspace";

const { Title, Text } = Typography;

const DRAG_KIND = "managed-list-row";

interface DragItem {
  id: number;
}

export interface ManagedListTableProps<T> {
  /** Title rendered in the card header. */
  title: string;
  /** Optional subtitle / explanation under the title. */
  description?: React.ReactNode;
  /** Full row list. Active rows are rendered in the main table in the
   *  order supplied; archived rows are auto-split into the collapse
   *  section. The parent decides the active-row order (typically by
   *  ``sort_order, id``). */
  rows: T[];
  /** Called with the new id order when the user drops a row. Active
   *  rows only; the component drops the dragged id from its current
   *  position and re-inserts at the drop index. The parent runs the
   *  PUT /reorder and refreshes ``rows`` from the response. */
  onReorder: (orderedIds: number[]) => Promise<void> | void;
  /** Opens the parent's create modal. */
  onCreate: () => void;
  /** Opens the parent's edit modal for ``row``. */
  onEdit: (row: T) => void;
  /** Archives (soft-delete) ``row``. The parent calls the entity's
   *  DELETE endpoint then refreshes. */
  onArchive: (row: T) => Promise<void> | void;
  /** Restores an archived row. Parent calls PATCH ``{active:true}``. */
  onRestore: (row: T) => Promise<void> | void;
  /** Domain-specific columns inserted between the label column and the
   *  actions column. (Pfand pages slot the cents column here.) */
  extraColumns?: ColumnsType<T>;
  /** Primary text accessor — rendered in the second column. */
  labelOf: (row: T) => string;
  /** True when the row is archived. Archived rows lose the drag handle
   *  and the Archive button (Restore appears in actions instead); they
   *  also collapse into the "Archived (N)" section. */
  isArchived: (row: T) => boolean;
  /** True when the row is a "system" row. Renders the inline ``system``
   *  tag next to the label and disables the Archive button. */
  isSystem: (row: T) => boolean;
  /** Stable row-id accessor (used as the table's ``rowKey`` and as the
   *  reorder payload). */
  keyOf: (row: T) => number;
  /** Disable the entire control surface (Add / Edit / Archive / Restore /
   *  DnD). Set during in-flight mutations to keep the optimistic state
   *  honest. */
  busy?: boolean;
  /** Add-button label (defaults to "+ Add"). */
  addLabel?: string;
  /** Tooltip text on the disabled Archive button for system rows. */
  systemArchiveTooltip?: string;
  /** Error message rendered above the table (e.g. mutation failure). */
  error?: string | null;
  /** Called when the user dismisses the error banner. */
  onErrorDismiss?: () => void;
  /** Forwarded to AntD's Table. */
  loading?: boolean;
}

/**
 * Wraps the table in a ``<DndProvider>`` so settings pages don't have to
 * remember to set one up themselves. The provider tolerates being nested
 * inside another (react-dnd v16 enforces this with a single backend
 * instance per provider via context); for our use the pages render at
 * the top of the routed tree so they own the only provider.
 */
export function ManagedListTable<T>(props: ManagedListTableProps<T>) {
  return (
    <DndProvider backend={MultiBackend} options={HTML5toTouch}>
      <ManagedListTableInner {...props} />
    </DndProvider>
  );
}

function ManagedListTableInner<T>({
  title,
  description,
  rows,
  onReorder,
  onCreate,
  onEdit,
  onArchive,
  onRestore,
  extraColumns,
  labelOf,
  isArchived,
  isSystem,
  keyOf,
  busy = false,
  addLabel = "+ Add",
  systemArchiveTooltip = "System rows cannot be archived.",
  error,
  onErrorDismiss,
  loading,
}: ManagedListTableProps<T>) {
  const activeRows = useMemo(
    () => rows.filter((r) => !isArchived(r)),
    [rows, isArchived],
  );
  const archivedRows = useMemo(
    () => rows.filter((r) => isArchived(r)),
    [rows, isArchived],
  );

  // Optimistic order for active rows. The user drops a row, we mutate
  // this state immediately, then call ``onReorder``; if the request
  // fails the parent re-sets ``rows`` (which we mirror via the effect
  // below) and the optimistic state snaps back to the server truth.
  const [optimisticOrder, setOptimisticOrder] = useState<number[]>(() =>
    activeRows.map(keyOf),
  );
  useEffect(() => {
    setOptimisticOrder(activeRows.map(keyOf));
  }, [activeRows, keyOf]);

  // Render order: ids from the optimistic order, mapped back through the
  // row list. Falls back to the raw active order if any ids drifted out
  // of sync — guards against a stale optimistic value referencing a row
  // the parent has since removed.
  const orderedActiveRows = useMemo(() => {
    const byId = new Map(activeRows.map((r) => [keyOf(r), r]));
    const ordered: T[] = [];
    const seen = new Set<number>();
    for (const id of optimisticOrder) {
      const r = byId.get(id);
      if (r) {
        ordered.push(r);
        seen.add(id);
      }
    }
    for (const r of activeRows) {
      if (!seen.has(keyOf(r))) ordered.push(r);
    }
    return ordered;
  }, [optimisticOrder, activeRows, keyOf]);

  const handleReorder = useCallback(
    (draggedId: number, targetId: number, position: "before" | "after") => {
      if (draggedId === targetId) return;
      setOptimisticOrder((prev) => {
        const next = prev.filter((id) => id !== draggedId);
        const targetIdx = next.indexOf(targetId);
        if (targetIdx === -1) return prev;
        next.splice(
          position === "before" ? targetIdx : targetIdx + 1,
          0,
          draggedId,
        );
        // Fire the PUT /reorder asynchronously.
        void Promise.resolve(onReorder(next));
        return next;
      });
    },
    [onReorder],
  );

  // Keyboard reorder: when the drag handle is focused, ArrowUp/ArrowDown
  // move the row by one position; Enter commits the order. The grip
  // button is the focus target — it has its own keyDown handler that
  // calls into ``moveRow``. We compute the destination index from the
  // current optimistic order so consecutive presses chain correctly.
  const moveRow = useCallback(
    (rowId: number, direction: "up" | "down") => {
      setOptimisticOrder((prev) => {
        const idx = prev.indexOf(rowId);
        if (idx === -1) return prev;
        const targetIdx = direction === "up" ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= prev.length) return prev;
        const next = [...prev];
        const [moved] = next.splice(idx, 1);
        next.splice(targetIdx, 0, moved);
        return next;
      });
    },
    [],
  );

  const commitKeyboardOrder = useCallback(() => {
    void Promise.resolve(onReorder(optimisticOrder));
  }, [onReorder, optimisticOrder]);

  // Build the column set once per render — cheap, and using useMemo
  // would just churn deps for the same result. The columns close over
  // stable handler refs.
  const columns: ColumnsType<T> = [
    {
      title: "",
      key: "drag",
      width: 40,
      render: (_: unknown, row: T) =>
        busy ? null : (
          <DragHandle
            rowId={keyOf(row)}
            onArrowUp={() => moveRow(keyOf(row), "up")}
            onArrowDown={() => moveRow(keyOf(row), "down")}
            onCommit={commitKeyboardOrder}
          />
        ),
    },
    {
      title: "Name",
      key: "name",
      render: (_: unknown, row: T) => (
        <Space size={6}>
          <Text>{labelOf(row)}</Text>
          {isSystem(row) && <Tag color="blue">system</Tag>}
        </Space>
      ),
    },
    ...(extraColumns ?? []),
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_: unknown, row: T) => (
        <Space size={4} wrap={false}>
          <Button size="small" disabled={busy} onClick={() => onEdit(row)}>
            Edit
          </Button>
          {isSystem(row) ? (
            <Tooltip title={systemArchiveTooltip}>
              <Button size="small" disabled>
                Archive
              </Button>
            </Tooltip>
          ) : (
            <Popconfirm
              title="Archive this row?"
              description="The row is soft-deleted; historical references stay intact."
              onConfirm={() => void onArchive(row)}
              disabled={busy}
            >
              <Button danger size="small" disabled={busy}>
                Archive
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Archived rows reuse the same column shape but drop the drag handle
  // and swap Archive→Restore in the action cell.
  const archivedColumns: ColumnsType<T> = [
    {
      title: "Name",
      key: "name",
      render: (_: unknown, row: T) => (
        <Space size={6}>
          <Text type="secondary">{labelOf(row)}</Text>
          {isSystem(row) && <Tag color="blue">system</Tag>}
        </Space>
      ),
    },
    ...(extraColumns ?? []),
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_: unknown, row: T) => (
        <Space size={4} wrap={false}>
          <Button size="small" disabled={busy} onClick={() => onEdit(row)}>
            Edit
          </Button>
          <Button
            size="small"
            disabled={busy}
            onClick={() => void onRestore(row)}
          >
            Restore
          </Button>
        </Space>
      ),
    },
  ];

  // Live snapshot of the visible rows + handlers for the DnD-wrapped
  // <tr> below. The same pattern as the catalog: rowsRef updates each
  // render so the row component can find its row by data-row-key, and
  // handlersRef carries the drop handler so DraggableRow can fire it.
  const rowMap = useMemo(() => {
    const m = new Map<string, T>();
    for (const r of orderedActiveRows) m.set(String(keyOf(r)), r);
    return m;
  }, [orderedActiveRows, keyOf]);
  const rowMapRef = useRef(rowMap);
  rowMapRef.current = rowMap;
  const handlersRef = useRef({ handleReorder, keyOf, busy });
  handlersRef.current = { handleReorder, keyOf, busy };

  // Wrap the active-table dataSource items in a ``{ key, row }`` shape
  // so we can use ``useStableTableComponents`` (which expects rows to
  // have a ``.key`` field). The data-row-key AntD sets on each <tr>
  // matches the ``rowKey`` callback below.
  type Wrapped = { key: string; row: T };
  const wrappedRows: Wrapped[] = orderedActiveRows.map((r) => ({
    key: String(keyOf(r)),
    row: r,
  }));
  const wrappedRowsRef = useRef(wrappedRows);
  wrappedRowsRef.current = wrappedRows;

  const tableComponents = useStableTableComponents<
    Wrapped,
    { handleReorder: typeof handleReorder; keyOf: typeof keyOf; busy: boolean }
  >({
    rowsRef: wrappedRowsRef,
    handlersRef,
    renderRow: (wrapped, handlers, rowProps) => (
      <DraggableRow
        rowId={wrapped ? handlers.keyOf(wrapped.row) : null}
        dragDisabled={handlers.busy || wrapped === undefined}
        onReorder={handlers.handleReorder}
        rowProps={rowProps}
      />
    ),
  });

  return (
    <Card>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            {title}
          </Title>
          {description && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">{description}</Text>
            </div>
          )}
        </div>
        <Button type="primary" disabled={busy} onClick={onCreate}>
          {addLabel}
        </Button>
      </Space>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable={!!onErrorDismiss}
          onClose={onErrorDismiss}
          style={{ marginBottom: 12 }}
        />
      )}
      <Table<Wrapped>
        columns={
          // Adapt the T-typed columns to the wrapper shape AntD sees.
          columns.map((col) => ({
            ...col,
            render: col.render
              ? (_: unknown, wrapped: Wrapped, idx: number) =>
                  (col.render as (
                    val: unknown,
                    row: T,
                    index: number,
                  ) => React.ReactNode)(undefined, wrapped.row, idx)
              : undefined,
          })) as ColumnsType<Wrapped>
        }
        dataSource={wrappedRows}
        rowKey={(wrapped) => wrapped.key}
        loading={loading}
        pagination={false}
        size="middle"
        components={tableComponents}
        locale={{ emptyText: "No active rows. Click Add to create one." }}
      />
      {archivedRows.length > 0 && (
        <Collapse
          ghost
          style={{ marginTop: 12 }}
          items={[
            {
              key: "archived",
              label: (
                <Space>
                  <Text strong>Archived</Text>
                  <Badge count={archivedRows.length} showZero={false} />
                </Space>
              ),
              children: (
                <Table<T>
                  columns={archivedColumns}
                  dataSource={archivedRows}
                  rowKey={(row) => keyOf(row)}
                  pagination={false}
                  size="middle"
                />
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Drag handle — focusable button with arrow-key reordering.
// ---------------------------------------------------------------------------

interface DragHandleProps {
  rowId: number;
  onArrowUp: () => void;
  onArrowDown: () => void;
  onCommit: () => void;
}

function DragHandle({ rowId, onArrowUp, onArrowDown, onCommit }: DragHandleProps) {
  void rowId;
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      onArrowUp();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      onArrowDown();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onCommit();
    }
  };
  return (
    <Tooltip title="Drag to reorder. Keyboard: focus, ArrowUp/Down to move, Enter to commit.">
      <button
        type="button"
        aria-label="Reorder row"
        onKeyDown={onKeyDown}
        style={{
          background: "transparent",
          border: "1px dashed transparent",
          padding: "2px 4px",
          cursor: "grab",
          color: "var(--ant-color-text-tertiary)",
          display: "inline-flex",
          alignItems: "center",
        }}
        // Mouse drag is handled by the wrapping <tr> via react-dnd; this
        // button just provides a focusable affordance for keyboard users
        // and a visible icon for everyone.
      >
        <DragOutlined />
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Draggable <tr> — wraps each row with useDrag + useDrop. Same liveRef
// trick as the catalog so react-dnd specs don't re-register mid-drag.
// ---------------------------------------------------------------------------

interface DraggableRowProps {
  rowId: number | null;
  dragDisabled: boolean;
  onReorder: (draggedId: number, targetId: number, position: "before" | "after") => void;
  rowProps: React.HTMLAttributes<HTMLTableRowElement> & {
    "data-row-key"?: string;
    children?: React.ReactNode;
  };
}

function DraggableRow({
  rowId,
  dragDisabled,
  onReorder,
  rowProps,
}: DraggableRowProps) {
  const trRef = useRef<HTMLTableRowElement>(null);
  // The drag/drop spec re-registration risk: same as the catalog. The
  // ``liveRef`` carries everything the spec needs at call-time, so the
  // hooks themselves can declare empty deps.
  const liveRef = useRef({ rowId, dragDisabled, onReorder });
  liveRef.current = { rowId, dragDisabled, onReorder };

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>(
    () => ({
      type: DRAG_KIND,
      canDrag: () => !liveRef.current.dragDisabled && liveRef.current.rowId !== null,
      item: () => {
        const id = liveRef.current.rowId;
        if (id === null) throw new Error("DraggableRow has no rowId");
        return { id };
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [],
  );

  const dropPosRef = useRef<"before" | "after">("before");
  const [dropPos, setDropPos] = useState<"before" | "after">("before");

  const [{ isOver, canDrop }, drop] = useDrop<
    DragItem,
    void,
    { isOver: boolean; canDrop: boolean }
  >(
    () => ({
      accept: DRAG_KIND,
      canDrop: (item) => {
        if (liveRef.current.dragDisabled) return false;
        return liveRef.current.rowId !== null && item.id !== liveRef.current.rowId;
      },
      hover: (_item, monitor) => {
        if (!trRef.current) return;
        const offset = monitor.getClientOffset();
        if (!offset) return;
        const rect = trRef.current.getBoundingClientRect();
        const pos: "before" | "after" =
          offset.y < rect.top + rect.height / 2 ? "before" : "after";
        if (pos !== dropPosRef.current) {
          dropPosRef.current = pos;
          setDropPos(pos);
        }
      },
      drop: (item) => {
        const id = liveRef.current.rowId;
        if (id === null) return;
        liveRef.current.onReorder(item.id, id, dropPosRef.current);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [],
  );

  drag(drop(trRef));

  const showInsertionLine = isOver && canDrop;

  const style: React.CSSProperties = {
    ...(rowProps.style ?? {}),
    opacity: isDragging ? 0.4 : 1,
    boxShadow: showInsertionLine
      ? dropPos === "after"
        ? "inset 0 -2px 0 0 var(--ant-color-primary, #1677ff)"
        : "inset 0 2px 0 0 var(--ant-color-primary, #1677ff)"
      : undefined,
  };

  const { children, ...rest } = rowProps;
  return (
    <tr {...rest} ref={trRef} style={style}>
      {children}
    </tr>
  );
}

